import { ReportCellType } from './cell-format';

/**
 * Per-report map of column name -> display type. This is the single,
 * framework-agnostic source of truth for how each report column is rendered,
 * consumed both by the Staff Site table (via the derived *Columns arrays the
 * components feed into app-generic-table-cell) and by the Node emailed-reports
 * renderer. Columns absent from the map are ReportCellType.text.
 */
export type ColumnTypeMap = { readonly [columnName: string]: ReportCellType };

// Custom is applied last so it wins over any standard type for the same column:
// a custom column bypasses standard formatting entirely.
const TYPE_PRECEDENCE: ReportCellType[] = [
  ReportCellType.objectId,
  ReportCellType.currency,
  ReportCellType.date,
  ReportCellType.custom,
];

/**
 * Build a ColumnTypeMap from columns grouped by type — a 1:1 replacement for the
 * per-report objectID/currency/date/customHTML arrays. Columns omitted are text.
 */
export function buildColumnTypeMap(
  groups: Partial<Record<ReportCellType, readonly string[]>>,
): ColumnTypeMap {
  const map: { [columnName: string]: ReportCellType } = {};
  for (const type of TYPE_PRECEDENCE) {
    for (const column of groups[type] || []) {
      map[column] = type;
    }
  }
  return map;
}

/** The display type for a column, defaulting to text when unmapped. */
export function cellTypeForColumn(columnTypes: ColumnTypeMap, column: string): ReportCellType {
  return columnTypes[column] ?? ReportCellType.text;
}

/** All columns of a given type — used to derive the *Columns arrays the table needs. */
export function columnsOfType(columnTypes: ColumnTypeMap, type: ReportCellType): string[] {
  return Object.keys(columnTypes).filter(column => columnTypes[column] === type);
}
